/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View, Pressable, Text, Avatar, DatetimeText } from '@components/atoms'

import { DashboardType } from '@schemas'
import { grey, blueGrey, deepPurple } from '@mui/material/colors'
import dayjs from 'dayjs'

interface PropsType {
	data: DashboardType
	onClick?: (data: any) => void
	target?: 'mainHome' | 'menu'
	isStatus?: boolean
}

export const DashboardCard = (props: PropsType) => {
	const { data } = props

	// variable
	const createdAt = dayjs(data.createdAt.split('.')[0]).format('YYYY-MM-DD HH:mm')
	const totalViews = `${data.viewsCount} Views`
	const target: 'mainHome' | 'menu' = props.target ?? 'menu'
	const status: string = props.isStatus && data.status !== undefined ? data.status : ''
	const statusName: string =
		status !== undefined && status === 'accepted'
			? '접수완료'
			: status !== undefined && status === 'completed'
			? '완료'
			: ''

	const onCardClick = React.useCallback(() => {
		if (props.onClick) {
			props.onClick(data)
		}
	}, [])

	return (
		<View css={style} className={`dashboardCard ${target}`}>
			<Pressable className="dashboardButton" onClick={onCardClick}>
				<View className="dashboardView">
					{target === 'menu' ? (
						<View className="dashboardContentView">
							<View className="dashboardMainContentView">
								<View className="dashboardMainContentInnerTopView">
									<Text category="s1" className="dashboardTitle">
										{data.title}
									</Text>
									{status !== '' ? (
										<Text category="c2" className="dashboardStatus">
											{statusName}
										</Text>
									) : null}
								</View>
								<View className="dashboardMainContentInnerView">
									<DatetimeText title={createdAt} color={grey[500]} category="c2" />
									<Text category="c1" className="dashboardInnerText">
										{data.createdByName}
									</Text>
									<Text category="c1" className="dashboardInnerText">
										{totalViews}
									</Text>
								</View>
							</View>
							<View className="dashboardAvatarView">
								<Avatar
									className="dashboardAvatar"
									picture={data.createdByImage}
									name={data.createdByName}
									color={data.createdByColor === '' ? blueGrey[100] : data.createdByColor}
									extpandingDisable={true}
								/>
							</View>
							<View className="dashboardSubcontentView">
								<Text category="c1" className="dashboardText">
									{data.createdByName}
								</Text>
							</View>
							<View className="dashboardSubcontentView">
								<Text category="c1" className="dashboardText">
									{totalViews}
								</Text>
							</View>
						</View>
					) : (
						<View className="dashboardContentView">
							<View className="dashboardMainContentView">
								<Text category="s1" className="dashboardTitle">
									{data.title}
								</Text>
								<DatetimeText title={createdAt} color={grey[500]} />
							</View>
						</View>
					)}
				</View>
			</Pressable>
		</View>
	)
}

const style = css({
	position: 'relative',
	width: '100%',
	background: 'white',
	boxSizing: 'border-box',

	'.dashboardButton': {
		width: '100%',
		height: 'fit-content',
		borderBottom: '0.8px solid',
		borderColor: grey['A400'],

		'.dashboardView': {
			width: '100%',
			height: 'fit-content',

			'.dashboardContentView': {
				display: 'flex',
				alignItem: 'center',
				justifyContent: 'space-between',
				width: '100%',
				height: '3.8rem',

				'.dashboardMainContentView': {
					width: '100%',
					marginLeft: '1rem',
					textAlign: 'left',

					'.dashboardMainContentInnerTopView': {
						display: 'flex',

						'.dashboardTitle': {
							width: 'fit-content',
							margin: 0,
							marginTop: '0.9rem',
						},

						'.dashboardStatus': {
							display: 'flex',
							justifyContent: 'center',
							alignItems: 'center',
							margin: 0,
							marginTop: '0.9rem',
							marginLeft: '0.5rem',
							padding: '0 0.3rem',
							width: 'fit-content',
							height: '1rem',
							borderRadius: '20px',
							color: 'white',
							backgroundColor: 'black',
							fontWeight: '700',
							fontSize: '11px',
						},
					},

					'.dashboardMainContentInnerView': {
						display: 'flex',
						margin: 0,
						marginTop: '0.4rem',

						'.dashboardCreateAt': {
							width: 'fit-content',
							margin: 0,
							marginTop: '0.5rem',
							color: grey[700],
						},
						'.dashboardInnerText': {
							display: 'none',
						},
					},
				},

				'.dashboardAvatarView': {
					paddingTop: '0.6rem',

					'.dashboardAvatar': {
						margin: 0,
					},
				},

				'.dashboardSubcontentView': {
					width: 'fit-content',
					minWidth: '10%',
					color: grey[700],
					textAlign: 'center',

					'.dashboardText': {
						margin: 0,
						marginTop: '1.5rem',
					},
				},
			},
		},
	},

	'&.mainHome': {
		marginBottom: '0.5rem',

		'.dashboardButton': {
			width: 'calc(100% - 0.1rem)',
			height: '5rem',
			borderRadius: '5px',
			borderLeft: '10px solid',
			borderLeftColor: deepPurple['A400'],
			boxShadow: '0px 2px 5px 0px rgb(0 0 0 / 25%)',

			'.dashboardContentView': {
				'.dashboardMainContentView': {
					marginLeft: '0.2rem',

					'.dashboardTitle': {
						fontSize: '12px',
					},
				},
			},
		},
	},

	'@media (max-width: 700px)': {
		'.dashboardButton': {
			'.dashboardView': {
				'.dashboardContentView': {
					'.dashboardMainContentView': {
						marginLeft: '0.5rem',

						'.dashboardMainContentInnerTopView': {
							'.dashboardStatus': {
								marginTop: '1.1rem',
							},
						},

						'.dashboardMainContentInnerView': {
							'.dashboardInnerText': {
								display: 'block',
								width: 'fit-content',
								marginLeft: '1rem',
								marginTop: '0.5rem',
								color: grey[700],
							},
						},
					},

					'.dashboardAvatarView': {
						display: 'none',
					},

					'.dashboardSubcontentView': { display: 'none' },
				},
			},
		},
	},

	'@media (max-width: 400px)': {
		'.dashboardButton': {
			'.dashboardView': {
				'.dashboardContentView': {
					height: '5rem',
					'.dashboardMainContentView': {
						'.dashboardMainContentInnerTopView': {
							'.dashboardTitle': {
								marginTop: '1.2rem',
								fontSize: '13px',
							},
						},
					},
				},
			},
		},
	},
})
